import { CognitoIdentityClient } from '@aws-sdk/client-cognito-identity';
import { fromCognitoIdentityPool as FromCognitoIdentityPool } from '@aws-sdk/credential-provider-cognito-identity';
import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
} from 'amazon-cognito-identity-js';

const UserPoolId = process.env.REACT_APP_AWS_USER_POOL_ID;
const ClientId = process.env.REACT_APP_AWS_CLIENT_ID;
const region = process.env.REACT_APP_AWS_REGION;
const identityPoolId = process.env.REACT_APP_AWS_IDENTITY_POOL_ID;
const identityProvider = `cognito-idp.${region}.amazonaws.com/${UserPoolId}`;

const poolData = {
  UserPoolId,
  ClientId,
};

const userPool =
  poolData.ClientId && poolData.UserPoolId ? new CognitoUserPool(poolData) : undefined;

let currentUser = userPool?.getCurrentUser();

export async function getSession() {
  if (!currentUser) {
    return false;
  }

  return new Promise((resolve, reject) => {
    currentUser?.getSession((err, session) => {
      if (err) {
        reject(err);
      } else {
        resolve(session);
      }
    });
  }).catch((err) => {
    throw err;
  });
}

// Module-level cache shared by every `curryCognitoCreds()` caller/curried
// function (BucketContext memoizes the outer call for the whole app session
// via `useMemo(..., [])`). `fromCognitoIdentityPool`'s returned provider
// does NOT memoize or refresh internally (checked against the installed
// @aws-sdk/credential-provider-cognito-identity — every call hits
// GetCredentialsForIdentity), so without this cache we'd either (a) freeze
// the first-fetched, ~1h-lived S3 credentials forever — the previous
// behaviour, which left images stuck on the "COLMAR" placeholder once they
// expired (Bug 8) — or (b) re-authenticate against Cognito on every single
// image load, which is what a naive "just always refetch" fix would do.
// This caches the resolved credentials and only refetches once they're
// close to expiring, deduping concurrent refreshes into one in-flight call.
let cachedCredentials;
let refreshPromise;

const CREDENTIALS_REFRESH_BUFFER_MS = 60_000;

function credentialsAreFresh(creds) {
  if (!creds?.expiration) return false;
  return new Date(creds.expiration).getTime() - CREDENTIALS_REFRESH_BUFFER_MS > Date.now();
}

async function resolveS3Credentials() {
  if (credentialsAreFresh(cachedCredentials)) {
    return cachedCredentials;
  }

  if (!refreshPromise) {
    refreshPromise = (async () => {
      const session = await getSession();
      const idToken = session?.idToken.jwtToken;

      const creds = new FromCognitoIdentityPool({
        client: new CognitoIdentityClient({
          region,
        }),
        identityPoolId,
        logins: {
          [identityProvider]: idToken,
        },
      });

      cachedCredentials = await creds();
      return cachedCredentials;
    })().finally(() => {
      refreshPromise = undefined;
    });
  }

  return refreshPromise;
}

export async function curryCognitoCreds() {
  return (fn) => async (val, key) => fn(await resolveS3Credentials(), val, key);
}

export function curryCognitoCreds2() {
  let credentials;

  getSession().then(async (res) => {
    const idToken = res?.idToken.jwtToken;

    const creds = new FromCognitoIdentityPool({
      client: new CognitoIdentityClient({
        region,
      }),
      identityPoolId,
      logins: {
        [identityProvider]: idToken,
      },
    });

    // FIX: MUST await the provider to get real credentials
    credentials = await creds();
  });

  return (fn) => (val, key) => fn(credentials, val, key);
}

export function getCurrentUser() {
  return currentUser;
}

function getCognitoUser(username) {
  const userData = {
    Username: username,
    Pool: userPool,
  };

  return new CognitoUser(userData);
}

export async function signUpUserWithEmail(username, email, password) {
  return new Promise((resolve, reject) => {
    const attributeList = [
      new CognitoUserAttribute({
        Name: 'email',
        Value: email,
      }),
    ];

    userPool.signUp(username, password, attributeList, [], (err, res) => {
      if (err) {
        reject(err);
      } else {
        resolve(res);
      }
    });
  }).catch((err) => {
    throw err;
  });
}

export async function signInWithEmail(username, password, callback) {
  return new Promise((resolve, reject) => {
    const authenticationData = {
      Username: username,
      Password: password,
    };
    const authenticationDetails = new AuthenticationDetails(authenticationData);

    currentUser = getCognitoUser(username);

    currentUser.authenticateUser(authenticationDetails, {
      onSuccess: (res) => {
        resolve(res);
      },
      onFailure: (err) => {
        reject(err);
      },
      newPasswordRequired: () => {
        callback();
      },
    });
  }).catch((err) => {
    throw err;
  });
}

export function completeNewPasswordChallenge(password, attributesData) {
  return new Promise((resolve, reject) => {
    currentUser.completeNewPasswordChallenge(password, attributesData, {
      onSuccess: (res) => {
        resolve(res);
      },
      onFailure: (err) => {
        reject(err);
      },
    });
  });
}

export function forgotPassword(email, callback) {
  const userData = {
    Username: email,
    Pool: userPool,
  };

  const cognitoUser = new CognitoUser(userData);

  return new Promise((resolve, reject) => {
    cognitoUser.forgotPassword({
      onSuccess: (res) => {
        resolve(res);
      },
      onFailure: (err) => {
        reject(err);
      },
      inputVerificationCode() {
        callback();
      },
    });
  });
}

export function confirmPassword(email, code, password) {
  const userData = {
    Username: email,
    Pool: userPool,
  };

  const cognitoUser = new CognitoUser(userData);

  return new Promise((resolve, reject) => {
    cognitoUser.confirmPassword(code, password, {
      onSuccess: (res) => {
        resolve(res);
      },
      onFailure: (err) => {
        reject(err);
      },
    });
  });
}

export function signOut() {
  if (currentUser) {
    currentUser.signOut();
  }
}

export async function getAttributes() {
  return new Promise((resolve, reject) => {
    currentUser?.getUserAttributes((err, attributes) => {
      if (err) {
        reject(err);
      } else {
        resolve(attributes);
      }
    });
  }).catch((err) => {
    throw err;
  });
}

export async function setAttribute(attribute) {
  return new Promise((resolve, reject) => {
    const attributeList = [];
    const res = new CognitoUserAttribute(attribute);
    attributeList.push(res);

    currentUser?.updateAttributes(attributeList, (err, result) => {
      if (err) {
        reject(err);
      } else {
        resolve(result);
      }
    });
  }).catch((err) => {
    throw err;
  });
}
